import { z } from "zod/v4";

import { StripeProductVersionList } from "../../schemas/stripe";
import {
  SubscriptionBillingProviderList,
  SubscriptionCollectionMethodList,
  SubscriptionProrationBehaviorList,
} from "../subscriptions";

import { StripeApiCouponDtoSchema } from "./coupon";
import { StripeApiCustomerDtoSchema } from "./customer";
import { StripeApiPriceDtoSchema } from "./price";
import { StripeApiProductDtoSchema } from "./product";

export const StripeApiCreateSubscriptionSettingDtoSchema = z.object({
  autoScale: z.boolean(),
  collectionMethod: z
    .enum(SubscriptionCollectionMethodList)
    .default("chargeAutomatically"),
  prorationBehavior: z.enum(SubscriptionProrationBehaviorList),
});

export type StripeApiCreateSubscriptionSettingDto = z.infer<
  typeof StripeApiCreateSubscriptionSettingDtoSchema
>;

export const StripeApiSubscriptionItemPairDtoSchema = z.object({
  price: StripeApiPriceDtoSchema.nullish(),
  product: StripeApiProductDtoSchema,
  quantity: z.number(),
  id: z.string().optional(),
  idx: z.string(),
  deleted: z.boolean(),
  isNew: z.boolean(),
  isReadOnly: z.boolean().optional(),
});

export type StripeApiSubscriptionItemPairDto = z.infer<
  typeof StripeApiSubscriptionItemPairDtoSchema
>;

export const StripeApiSubscriptionSchedulePhaseDtoSchema = z.object({
  phaseStart: z.number(),
  phaseEnd: z.number().nullish(),
  items: StripeApiSubscriptionItemPairDtoSchema.array(),
  coupon: StripeApiCouponDtoSchema.nullish(),
  trialEndTs: z.number().nullish(),
});

export type StripeApiSubscriptionSchedulePhaseDto = z.infer<
  typeof StripeApiSubscriptionSchedulePhaseDtoSchema
>;

export const StipeApiSubscriptionScheduleDtoSchema = z.object({
  id: z.string(),
  phases: StripeApiSubscriptionSchedulePhaseDtoSchema.array(),
});

export type StipeApiSubscriptionScheduleDto = z.infer<
  typeof StipeApiSubscriptionScheduleDtoSchema
>;

const BaseStripeApiSubscriptionDtoSchema = z.object({
  id: z.string(),
  type: z.enum(["withPhases", "withoutPhases"]),
  status: z.string(),
  currentPeriodEnd: z.number(),
  billingProvider: z.enum(SubscriptionBillingProviderList),
  customer: StripeApiCustomerDtoSchema.nullish(),
  settings: StripeApiCreateSubscriptionSettingDtoSchema,
  phases: StripeApiSubscriptionSchedulePhaseDtoSchema.array(),
  cancelAt: z.number().nullish(),
});

export const StripeApiSubscriptionWithoutPhasesDtoSchema =
  BaseStripeApiSubscriptionDtoSchema.extend({
    type: z.literal("withoutPhases"),
  });

export type StripeApiSubscriptionWithoutPhasesDto = z.infer<
  typeof StripeApiSubscriptionWithoutPhasesDtoSchema
>;

export const StripeApiSubscriptionWithPhasesDtoSchema =
  BaseStripeApiSubscriptionDtoSchema.extend({
    type: z.literal("withPhases"),
    scheduleId: z.string(),
  });

export const StripeApiSubscriptionDtoSchema = z.discriminatedUnion("type", [
  StripeApiSubscriptionWithoutPhasesDtoSchema,
  StripeApiSubscriptionWithPhasesDtoSchema,
]);

export type StripeApiSubscriptionDto = z.infer<
  typeof StripeApiSubscriptionDtoSchema
>;

export const StripeApiSubscriptionLinkDtoSchema = z.object({
  id: z.string(),
  subscriptionLinkId: z.uuid(),
  name: z.string(),
});

export type StripeApiSubscriptionLinkDto = z.infer<
  typeof StripeApiSubscriptionLinkDtoSchema
>;
